import { Button, Container, IconButton, Tooltip, Typography, Drawer, Box, Divider, Grid } from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import { makeStyles } from '@material-ui/core/styles';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPurchaseRequestEmit } from '../../app/services/purchase-request/create-purchase-request.api';
import { getDraftPurchaseRequestByUser } from '../../app/services/purchase-request/get-purchase-purchase.api';
import { customtheme } from '../../app/theme/theme';
import EnhancedTable, { HeadCell } from '../../common/components/EnhancedTable';
import PurchaseRequestEmitDrawer from './PurchaseRequestEmitDrawer';
import DialogSearch from '../../common/components/DialogSearch';
import { handleCheckPrevValue } from '../../common/functions/checkPrevValue';
import { DialogSearchData } from '../PurchaseOrderEmit';
import ErrorIcon from '@material-ui/icons/Error';
import { mapperToPurchaseRequest } from '../../app/mappers/purchase-request/purchase-request.mapper';

const useStyles = makeStyles(() => ({
  root: { backgroundColor: customtheme.gray[200], display: 'flex', flexDirection: 'column', flex: 1 },
  tableSection: { padding: 24, flex: 1, display: 'flex' },
  tableSectionContainer: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column', maxWidth: '100%' },
  drawer: { width: 560, maxWidth: '100vw' },
  drawerHeader: { padding: 16, borderBottom: `1px solid #ddd`, background: '#f8f9fb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  drawerContent: { padding: 16, overflowY: 'auto', height: '100%', background: '#f3f5f8' },
  drawerSummary: { background: '#ffffff', border: '1px solid #dfe5ec', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)' },
  summaryLabel: { color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 },
  summaryValue: { fontWeight: 700, color: '#111827' },
  itemCard: { background: '#ffffff', border: '1px solid #dfe5ec', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)' },
  itemTitle: { fontWeight: 700, color: '#111827', marginBottom: 4 },
  itemMeta: { color: '#475569' },
  drawerFooter: { padding: 16, borderTop: `1px solid #eee`, background: customtheme.gray[200], display: 'flex', justifyContent: 'flex-end' }
}));

const PurchaseRequestEmit = () => {
  const classes = useStyles();
  const [selectedData, setSelectedData] = useState<any>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState({open: false, btnTitle: 'Voltar', btnType: 'back'});
  const [viewDrawer, setViewDrawer] = useState<{ open: boolean; data: any | null }>({ open: false, data: null });
  const fieldsInit = mapperToPurchaseRequest({})
  const [fields, setFields] = useState<any[]>(fieldsInit);
  const [checkPrevCompanyValue, setCheckPrevCompanyValue] = useState({
    exists: false,
    value: ''
  })
  const [checkPrevBranchValue, setCheckPrevBranchValue] = useState({
    exists: false,
    value: ''
  })
  const [isDialogSearchOpen, setIsDialogSearchOpen] = useState(false);
  const [dialogDataSearch, setDialogDataSearch] = useState<DialogSearchData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSnackbar, setIsSnackbar] = useState({ open: false, message: '', title: '' });

  const headCells: HeadCell[] = [
    { id: 'numSol', numeric: false, disablePadding: true, label: 'Numero' },
    { id: 'sigemp', numeric: false, disablePadding: true, label: 'Empresa' },
    { id: 'nomfil', numeric: false, disablePadding: true, label: 'Filial' },
    { id: 'itensCount', numeric: true, disablePadding: false, label: 'Itens' },
    { id: 'valortotal', numeric: true, disablePadding: false, label: 'Valor' },
    { id: 'datemi', numeric: false, disablePadding: true, label: 'Data' },
    { id: 'actions', numeric: true, disablePadding: false, label: 'Ações' },
  ];

  const createData = (p: any) => ({
    id: p.id,
    numSol: p.numsol || '',
    sigemp: p.nomemp || '',
    nomfil: p.nomfil || '',
    itensCount: Array.isArray(p.itens) ? p.itens.length : 0,
    valortotal: Array.isArray(p.itens) ? p.itens.reduce((acc: number, item: any) => acc + (item.qtdsol * item.presol || 0), 0) : 0,
    datemi: p.criado_em ? new Date(p.criado_em).toLocaleDateString('pt-BR') : '',
    itens: p.itens || [],
    ...p,
  });

  // include itensCount for the table header
  const createDataWithItems = (p: any) => ({ ...createData(p), itensCount: Array.isArray(p.itens) ? p.itens.length : 0 });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (value: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  };

  const { isFetching, refetch } = useQuery(['purchaseRequestEmit'], async () => {
    const token = localStorage.getItem('token');
    if (token) {
      const res = await getDraftPurchaseRequestByUser(token, { take: rowsPerPage, skip: page * rowsPerPage });
      if (res) {
        setSelectedData(res.results || []);
        setCount(res.total || 0);
      }
    }
    return [];
  }, { refetchOnWindowFocus: false });

  useEffect(() => { refetch(); }, [page, refetch]);

  const handleOpenCreateDrawer = () => {
    setSelected([]);
    setFields(fieldsInit);
    setIsDrawerOpen({open: true, btnTitle: 'Salvar', btnType: 'save'});
  };

  const Alert = (props: AlertProps) => {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  }

  const handleEmptyField = (val: any, type: string) => {
    if(val){
      return type === 'string' ? val+'' : val;
    } else {
      return null;
    } 
  }

  const handleFormatedDataToDatabase = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token');

      let data = {} as any;
      // minimal payload: itens as array of objects
      const itens = fields.filter((field) => field.id === 'itens')[0].value.map((item:any, index: number) => {
        const produto = item.filter((field:any) => field.id === 'produto')[0].value;
        const servico = item.filter((field:any) => field.id === 'servico')[0].value;
        const derivacao = item.filter((field:any) => field.id === 'derivacao')[0].value;
        const tipo = item.filter((field:any) => field.id === 'tipo')[0].value;
        const datprv = item.filter((field:any) => field.id === 'datprv')[0].value;
        const unimed = item.filter((field:any) => field.id === 'unimed')[0].value;
        const qtdsol = item.filter((field:any) => field.id === 'qtdsol')[0].value;
        const presol = item.filter((field:any) => field.id === 'presol')[0].value;
        const seqsol = item.filter((field:any) => field.id === 'seqsol')[0].value;
        const deposito = item.filter((field:any) => field.id === 'deposito')[0].value;
        const centrosCusto = item.filter((field:any) => field.id === 'centrosCusto')[0].value;
        const transacao = item.filter((field:any) => field.id === 'transacao')[0].value;

        console.log(item)

        let obj = {} as any;
        obj['tipo'] = handleEmptyField(tipo?.tipo, 'string');

        if (tipo?.tipo === 'produto') {
          obj['coditem'] = handleEmptyField(produto?.codpro, 'number');
          obj['desitem'] = handleEmptyField(produto?.despro, 'string');
        } else {
          obj['coditem'] = handleEmptyField(servico?.codser, 'number');
          obj['desitem'] = handleEmptyField(servico?.desser, 'string');
        }
        if(derivacao){
          obj['codder'] = handleEmptyField(derivacao?.codder, 'number');
          obj['desder'] = handleEmptyField(derivacao?.desder, 'string');
        }
        obj['coddep'] = handleEmptyField(deposito?.coddep, 'string');
        obj['desdep'] = handleEmptyField(deposito?.desdep, 'string');
        obj['codccu'] = handleEmptyField(centrosCusto?.codccu, 'number');
        obj['desccu'] = handleEmptyField(centrosCusto?.desccu, 'string');
   
        obj['datprv'] = handleEmptyField(datprv?.datprv, 'string');
        obj['unimed'] = handleEmptyField(unimed, 'string');
        obj['qtdsol'] = handleEmptyField(qtdsol?.qtdsol, 'number');
        obj['seqsol'] = handleEmptyField(seqsol?.seqsol, 'number');
        obj['presol'] = handleEmptyField(presol?.presol, 'number');
        obj['codtns'] = handleEmptyField(transacao?.codtns, 'string');
        obj['destns'] = handleEmptyField(transacao?.destns, 'string');

        return obj;
      })
      data.itens = itens;

    const company = fields.filter((field) => field.id === 'dados')[0].value;
    const filial = fields.filter((field) => field.id === 'filiais')[0].value;
    data.codemp = handleEmptyField(company?.codemp, 'number')
    data.nomemp = handleEmptyField(company?.nomemp, 'string')
    data.codfil = handleEmptyField(filial?.codfil, 'number')
    data.nomfil = handleEmptyField(filial?.nomfil, 'string')

      const res = await createPurchaseRequestEmit(data, token);
      console.log(res);
      setIsLoading(false);
      if (res && !res.isError && (!res.statusCode || res.statusCode < 400)) {
        const createdRequest = res as any;
        const purchaseRequestNumber = createdRequest?.data?.numsol ?? createdRequest?.data?.numero ?? createdRequest?.numsol ?? createdRequest?.numero;
        setIsSnackbar({
          open: true,
          message: purchaseRequestNumber
            ? `Solicitação de Compra gerada com sucesso: número ${purchaseRequestNumber}`
            : 'Solicitação de Compra gerada com sucesso',
          title: 'success',
        });
        await refetch();
        setIsDrawerOpen({open: false, btnTitle: 'Salvar', btnType: 'save'});
      } else {
        setIsSnackbar({
          open: true,
          message: res?.message || 'Erro ao salvar solicitação',
          title: 'error',
        });
      }
    } catch (err) {
      console.log(err);
      setIsLoading(false);
      setIsSnackbar({ open: true, message: 'Erro ao salvar solicitação', title: 'error' });
    }
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen({open: false, btnTitle: 'Salvar', btnType: 'save'});
    setSelected([]);
    setFields(fieldsInit);
  };

  // edit removed — view-only

  const handleOpenDialogSearch = (data: any) => {
    setIsDialogSearchOpen(true);
    setDialogDataSearch(data);
  };

  const handleUpdateField = (data: any) => {
    const update = [...fields];
    update[update.findIndex((field) => field.id === dialogDataSearch?.id)].value = 
      data[dialogDataSearch?.key || ''] === 'Nenhum item' ? null : data
    setFields(update)
    setIsDialogSearchOpen(false);
    setDialogDataSearch(null);
    if(dialogDataSearch?.id === 'dados')
      setCheckPrevCompanyValue(handleCheckPrevValue('dados', update, 'codemp'))
    if(dialogDataSearch?.id === 'filiais')
      setCheckPrevBranchValue(handleCheckPrevValue('filiais', update, 'codfil'))
  }

  const handleCheckFields = () => {
    const requireds = fields.filter((field) => field.required);

    const hasValue = (value: any): boolean => {
      if (value === null || value === undefined) return false;

      if (typeof value === 'string') return value.trim() !== '';

      if (Array.isArray(value)) return value.length > 0;

      if (typeof value === 'object') {
        return Object.values(value).some((v) => {
          if (v === null || v === undefined) return false;
          if (typeof v === 'string') return v.trim() !== '';
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === 'object') return hasValue(v);
          return true;
        });
      }

      return true;
    };

    const hasAllRequired = requireds.every((required) => hasValue(required.value));

    if (!hasAllRequired) {
      setIsSnackbar({ open: true, message: 'Por favor, adicione os campos obrigatórios!', title: 'error' });
    } else {
      handleFormatedDataToDatabase()
    }
  };

  const handleCloseDialogSearch = () => {
    setIsDialogSearchOpen(false);
    setDialogDataSearch(null);
  };

  return (
    <div className={classes.root}>
      <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Solicitações de Compra</Typography>
        <Button onClick={handleOpenCreateDrawer} style={{ background: customtheme.blue[500], color: '#fff' }}>Nova Solicitação</Button>
      </div>
      <div className={classes.tableSection}>
        <Container className={classes.tableSectionContainer}>
          <EnhancedTable
            title=""
            rowsPerPageOptions={[]}
            isClickable
            data={selectedData?.map((data:any) => createDataWithItems(data) )}
            count={count}
            orderBy={'datemi'}
            setOrderBy={() => {}}
            headCells={headCells}
            isLoading={isFetching}
            selected={selected}
            setSelected={setSelected}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            buttonsActions={['info']}
            handleInfo={(id) => {
               const item = selectedData.find((d:any) => d.id === id);
                  setViewDrawer({ open: true, data: item });
            }}
            buttonsSelected={[
              selected.length === 1 && (
                <Tooltip
                  title="Info"
                  onClick={() => {
                  const item = selectedData.find((d:any) => d.id === selected[0]);
                  setViewDrawer({ open: true, data: item });
                }}
                  key='info'
                >
                <IconButton aria-label="info">
                  <ErrorIcon style={{color: customtheme.white}}/>
                </IconButton>
              </Tooltip>
              ),
            ]}
          />
        </Container>
      </div>

      <PurchaseRequestEmitDrawer
        open={isDrawerOpen.open}
        onClose={handleCloseDrawer}
        fields={fields}
        setFields={setFields}
        isLoading={isLoading}
        handleOpenDialogSearch={handleOpenDialogSearch}
        checkPrevCompanyValue={checkPrevCompanyValue}
        checkPrevBranchValue={checkPrevBranchValue}
        handleCheckFields={handleCheckFields}
        setIsSnackbar={setIsSnackbar}
      />
      <Drawer anchor="right" open={viewDrawer.open} onClose={() => setViewDrawer({ open: false, data: null })}>
        <Box width={560} maxWidth="100vw" display="flex" flexDirection="column" height="100%">
          <Box p={2} className={classes.drawerHeader}>
            <Box>
              <Typography variant="h6">Solicitação de Compra</Typography>
              <Typography variant="body2" color="textSecondary">
                {viewDrawer.data?.numsol ? `Nº ${viewDrawer.data.numsol}` : 'Detalhes da solicitação'}
              </Typography>
            </Box>
            <IconButton onClick={() => setViewDrawer({ open: false, data: null })}>
              <ErrorIcon style={{ color: customtheme.gray[400], transform: 'rotate(45deg)' }} />
            </IconButton>
          </Box>

          <Box className={classes.drawerContent}>
            <Box className={classes.drawerSummary}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.summaryLabel}>Empresa</Typography>
                  <Typography className={classes.summaryValue}>{viewDrawer.data?.nomemp || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.summaryLabel}>Filial</Typography>
                  <Typography className={classes.summaryValue}>{viewDrawer.data?.nomfil || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography className={classes.summaryLabel}>Número</Typography>
                  <Typography className={classes.summaryValue}>{viewDrawer.data?.numsol || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography className={classes.summaryLabel}>Data</Typography>
                  <Typography className={classes.summaryValue}>{formatDate(viewDrawer.data?.criado_em)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography className={classes.summaryLabel}>Total</Typography>
                  <Typography className={classes.summaryValue}>{formatCurrency(Array.isArray(viewDrawer.data?.itens) ? viewDrawer.data.itens.reduce((acc: number, item: any) => acc + ((item.qtdsol || 0) * (item.presol || 0)), 0) : 0)}</Typography>
                </Grid>
              </Grid>
            </Box>

            <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Itens</Typography>
              <Typography variant="body2" color="textSecondary">
                {Array.isArray(viewDrawer.data?.itens) ? `${viewDrawer.data.itens.length} item(ns)` : '0 item(ns)'}
              </Typography>
            </Box>
            <Divider style={{ marginBottom: 16 }} />

            {viewDrawer.data && Array.isArray(viewDrawer.data.itens) && viewDrawer.data.itens.length === 0 && (
              <Box className={classes.itemCard}>
                <Typography>Nenhum item</Typography>
              </Box>
            )}

            {viewDrawer.data && Array.isArray(viewDrawer.data.itens) && viewDrawer.data.itens.map((it: any, idx: number) => {
              const desc = it.desitem || it.despro || (Array.isArray(it) && it.find((f:any)=>f.id==='produto')?.value?.despro) || '';
              const code = it.coditem || it.codpro || (Array.isArray(it) && it.find((f:any)=>f.id==='produto')?.value?.codpro) || '';
              const qtd = it.qtd || it.qtdsol || (Array.isArray(it) && it.find((f:any)=>f.id==='qtdsol')?.value?.qtdsol) || '';
              const uni = it.unimed || it.unidademedida || (Array.isArray(it) && it.find((f:any)=>f.id==='unimed')?.value) || '';
              const datprv = it.datprv || (Array.isArray(it) && it.find((f:any)=>f.id==='datprv')?.value?.datprv) || '';
              const presol = it.presol || (Array.isArray(it) && it.find((f:any)=>f.id==='presol')?.value?.presol) || 0;
              const val = it.valortotal ?? it.valor ?? ((Number(qtd) || 0) * (Number(presol) || 0));
              return (
                <Box key={idx} className={classes.itemCard}>
                  <Typography variant="subtitle1" className={classes.itemTitle}>{desc || 'Item sem descrição'}</Typography>
                  <Typography variant="body2" className={classes.itemMeta}>Código: {code || '-'}</Typography>
                  <Typography variant="body2" className={classes.itemMeta}>Qtd: {qtd || '-'} • Uni: {uni || '-'}</Typography>
                  <Typography variant="body2" className={classes.itemMeta}>Data prevista: {formatDate(datprv)}</Typography>
                  <Typography variant="body2" className={classes.itemMeta}>Valor total: {formatCurrency(typeof val === 'number' ? val : Number(val) || 0)}</Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Drawer>
      <DialogSearch
        title={dialogDataSearch?.title || ''}
        id={dialogDataSearch?.id || ''}
        keyItem={dialogDataSearch?.key || ''}
        keySubItem={dialogDataSearch?.keySub || ''}
        isDialogOpen={isDialogSearchOpen}
        isPrevSearch={dialogDataSearch?.isPrevSearch || false}
        prevSearch={dialogDataSearch?.prevSearch || []}
        prevDataSearch={dialogDataSearch?.prevDataSearch}
        handleCloseDialog={handleCloseDialogSearch}
        handleUpdateField={(data:any) => { handleUpdateField(data) }}
      />
      <Snackbar open={isSnackbar.open} autoHideDuration={6000} onClose={() => setIsSnackbar({ ...isSnackbar, open: false })}>
        <Alert onClose={() => setIsSnackbar({ ...isSnackbar, open: false })} severity={isSnackbar.title as any}>
          {isSnackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PurchaseRequestEmit;
